import { HubConnectionState } from '@microsoft/signalr';
import { useCallback, useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { INVENTORY_BULK_UPDATE_BARCODES_KEY } from 'src/components/Listings/InventoryActionDropdown';
import {
  barcodeDictToArray,
  BulkBarcodeForm,
} from 'src/components/UploadArtifacts/UploadBarcodes/UploadBarcodes.utils';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useBulkEditHubContext } from 'src/contexts/BulkEditHubContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkActionType,
  BulkEditListingClient,
  BulkEditProgress,
  BulkEditStep,
  Event,
  ListingQuery,
  TicketBarcode,
} from 'src/WebApiController';

import { BulkUploadBarcodes } from './BulkUploadBarcodes';
import { useBulkUpdateListingBarcodesPreviewQuery } from './useBulkUpdateListingBarcodesPreviewQuery';

export const BulkUploadListingBarcodesModal = ({
  event,
  filterQueryWithEventIds,
}: {
  event?: Event;
  filterQueryWithEventIds: ListingQuery;
}) => {
  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const { closeModal } = useContext(ModalContext);

  const bulkUpdateBarcodesPreviewQuery =
    useBulkUpdateListingBarcodesPreviewQuery(filterQueryWithEventIds);

  const methods = useForm<BulkBarcodeForm>({
    defaultValues: {
      isBulkUpload: false,
      barcodes: [],
    },
  });

  const { showErrorDialog } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const { setSelectionMode } = useMultiSelectionContext();

  const { setProgress, setPreview, setMainDialogOpened } =
    useBulkEditHubContext();

  const updateKey = event
    ? `${INVENTORY_BULK_UPDATE_BARCODES_KEY}-${event.virtId}`
    : INVENTORY_BULK_UPDATE_BARCODES_KEY;

  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onBulkEditDone = useCallback(
    async (doneProgress: BulkEditProgress, finalErrors: string[]) => {
      if (finalErrors.length === 0 && doneProgress.step === BulkEditStep.Done) {
        setActivePosEntity(0);
        if (refreshExpandedListItems) {
          await refreshExpandedListItems();
        }
        setSelectionMode(undefined);
      }
    },
    [refreshExpandedListItems, setActivePosEntity, setSelectionMode]
  );

  const { bulkEditHub, progress, preview, initJob } = useBulkEditHub(
    ActionOutboxEntityType.Listing,
    BulkActionType.UpdateBarcodes,
    updateKey,
    onBulkEditDone
  );

  const hasBackgroundBulkEditFeature =
    bulkEditHub?.state === HubConnectionState.Connected;

  useEffect(() => {
    bulkUpdateBarcodesPreviewQuery.refetch();
    initJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (bulkUpdateBarcodesPreviewQuery.data) {
      methods.reset({
        isBulkUpload: false,
        barcodes: barcodeDictToArray(bulkUpdateBarcodesPreviewQuery.data),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkUpdateBarcodesPreviewQuery.data]);

  const isLoading =
    bulkUpdateBarcodesPreviewQuery.isLoading ||
    bulkUpdateBarcodesPreviewQuery.isFetching;

  const onClose = useCallback(() => {
    setMainDialogOpened(false);
    if (progress) {
      setSelectionMode();
    }
    closeModal(true);
  }, [setMainDialogOpened, progress, closeModal, setSelectionMode]);

  const onSubmit = useCallback(
    async (allBarcodes: { [key: string]: TicketBarcode[] }) => {
      setIsSubmitting(true);
      await tryInvokeApi(
        async () => {
          const succeeded = await new BulkEditListingClient(
            activeAccountWebClientConfig
          ).bulkUpdateBarcodes(
            allBarcodes,
            updateKey,
            hasBackgroundBulkEditFeature
          );

          if (!hasBackgroundBulkEditFeature) {
            if (succeeded) {
              setSelectionMode();
              await refreshExpandedListItems();
            }

            closeModal();
          }
        },
        (error) => {
          showErrorDialog('BulkEditListingClient.bulkUpdateBarcodes', error, {
            trackErrorData: {
              preview,
              allBarcodes,
            },
          });
        },
        () => {
          if (!hasBackgroundBulkEditFeature) {
            setIsSubmitting(false);
          }
        }
      );
      setIsSubmitting(false);
    },
    [
      activeAccountWebClientConfig,
      closeModal,
      hasBackgroundBulkEditFeature,
      preview,
      refreshExpandedListItems,
      setSelectionMode,
      showErrorDialog,
      updateKey,
    ]
  );

  return (
    <FormProvider {...methods}>
      <BulkUploadBarcodes
        updateKey={updateKey}
        event={event}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
        onSubmit={onSubmit}
        onClose={onClose}
        entityType={ActionOutboxEntityType.Listing}
      />
    </FormProvider>
  );
};
